import { useSelector } from 'react-redux';
import { TextInput } from '@makemydeal/dr-shared-components/text-input';
import { PhoneInput } from '@makemydeal/dr-shared-components/phone-input';
import { Typography } from '@interstate/components/Typography';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCustomerCardContext } from '../../CustomerFormContext';
import { RevealableMaskTextInput } from '@makemydeal/dr-shared-components';
import { useGetSpd } from '../../../hooks/useGetSpd';
import { useCustomerCardSelector } from '../../../hooks/useCustomerCardSelector';
import * as customerSpdSelectors from '../../../store/customerSpdSelectors';
import {
    BANK_NAME_FIELD,
    BANK_CONTACT_NAME_FIELD,
    BANK_CONTACT_PHONE_FIELD,
    BANK_ACCOUNT_NUMBER_FIELD,
    CHECKING_ACCOUNT_NUMBER_MASK
} from '../../../constants';
import { FieldsRow } from '../sectionLayout.styles';
import { getFieldError } from './businessErrors.utils';

const BankInformation = () => {
    const { formValues, handleInputChange, handlePhoneInputChange, isLocked, formErrors, spdValues, setSpdValue, capabilities } =
        useCustomerCardContext();
    const canDesk = useCustomerCardSelector(customerSpdSelectors.getHasDeskingPermission);
    const businessErrors = formErrors?.business;
    const getError = (field: string) => getFieldError(businessErrors, field);

    const ccid = formValues.commonConsumerId;
    const presenceSpdData = useCustomerCardSelector(customerSpdSelectors.getSpdPresence(ccid));
    const spdCheckError = useSelector(customerSpdSelectors.getSpdCheckError(ccid));
    const { fetchSpd, isGetLoading } = useGetSpd(ccid);
    const bankAccountNumber = spdValues.bankAccountNumber;
    const showBankAccountNumber = Boolean(ccid && !spdCheckError);
    const canRevealBankAccountNumber = capabilities?.enableSpdReveal === false ? false : canDesk;

    return (
        <div data-testid="bank-information-section">
            <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }} data-testid="bank-information-header">
                Bank Information
            </Typography>
            <FieldsRow>
                <TextInput
                    name="business-bankName"
                    displayLabel={true}
                    label="Bank Name"
                    value={formValues.business?.bankName || ''}
                    onChange={(e) => handleInputChange(BANK_NAME_FIELD, e.target.value)}
                    data-testid="business-info-bank-name"
                    size="small"
                    disabled={isLocked}
                    hasError={getError('bankName').hasError}
                    errorMessage={getError('bankName').errorMessage}
                    selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                />
                <TextInput
                    name="business-bankContactName"
                    displayLabel={true}
                    label="Bank Contact Name"
                    value={formValues.business?.bankContactName || ''}
                    onChange={(e) => handleInputChange(BANK_CONTACT_NAME_FIELD, e.target.value)}
                    data-testid="business-info-bank-contact-name"
                    size="small"
                    disabled={isLocked}
                    hasError={getError('bankContactName').hasError}
                    errorMessage={getError('bankContactName').errorMessage}
                    selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                />
                <PhoneInput
                    name="business-bankContactPhoneNumber"
                    label="Bank Phone"
                    value={formValues.business?.bankContactPhoneNumber || ''}
                    onChange={(e) => handlePhoneInputChange(BANK_CONTACT_PHONE_FIELD, e)}
                    data-testid="business-info-bank-phone"
                    size="sm"
                    disabled={isLocked}
                    selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                />
                {showBankAccountNumber && (
                    <RevealableMaskTextInput
                        name="business-checkingAccountNumber"
                        label="Checking Account Number"
                        data-testid="business-info-checking-account-number"
                        maskedValue={CHECKING_ACCOUNT_NUMBER_MASK}
                        value={bankAccountNumber ?? ''}
                        defaultMask={!!(presenceSpdData?.bankAccountNumber || bankAccountNumber)}
                        loading={isGetLoading}
                        canReveal={canRevealBankAccountNumber}
                        onToggle={(next) => {
                            if (next) fetchSpd();
                        }}
                        onChange={(_field, value) => setSpdValue(BANK_ACCOUNT_NUMBER_FIELD, value)}
                        disabled={isLocked}
                        hasError={Boolean(formErrors?.bankAccountNumber?.hasError)}
                        errorMessage={formErrors?.bankAccountNumber?.errorMessage ?? ''}
                    />
                )}
            </FieldsRow>
        </div>
    );
};

BankInformation.displayName = 'BankInformation';
export default BankInformation;
